
import Vue from "vue";
import { Component, Prop, Ref } from "vue-property-decorator";
import { uiStore } from "@/store/store-accessor";
import PreviewWidthAdjustmentHandle from "@/components/Reusable/Iframe/PreviewWidthAdjustmentHandle.vue";
import Loading from "@/components/Reusable/Loading.vue";
import { CanvasPreviewWidth, InterfaceState } from "@/types/ui";
import StandardButton from "@/components/UI/StandardButton.vue";
import { Breakpoint } from "@/types/breakpoints";

const CANVAS_PADDING = 8;

@Component({
  components: {
    StandardButton,
    Loading,
    PreviewWidthAdjustmentHandle
  }
})
export default class IframeWrapper extends Vue {
  @Prop() iframeState!: InterfaceState;
  @Prop() iframePreviewWidth!: CanvasPreviewWidth;
  @Prop() iframeUrl!: string;
  @Prop() iframeId!: string;
  @Prop() minWidth!: number;
  @Prop() iframeExited!: boolean;
  @Prop() currentBreakpoint!: Breakpoint;

  @Ref() readonly mainWrapper!: HTMLDivElement | null;
  @Ref() readonly iframeWrapper!: HTMLDivElement | null;
  @Ref() readonly iframe!: HTMLIFrameElement | null;

  iframeWrapperPosition = {
    x: 0,
    y: 0,
    width: 0,
    height: 0
  };

  get canvasPadding() {
    return this.isPreviewMode ? 0 : CANVAS_PADDING;
  }

  get mainWrapperStyle() {
    return {
      padding: `${this.canvasPadding}px`
    };
  }

  get iframeWrapperStyles() {
    return {
      width:
        this.iframePreviewWidth.width === "100%"
          ? "100%"
          : `${this.iframePreviewWidth.width}px`,
      overflow:
        uiStore.settings.scaleToFit === "SCALED_TO_FIT" ? "hidden" : "auto"
    };
  }

  get iframeStyle() {
    const scaledStyles = (width: number) => {
      const scaleFactor = Math.min(
        1,
        this.iframePreviewWidth.availableCanvasSize.width / width
      );
      return {
        width: `${width}px`,
        transform: `scale(${scaleFactor})`,
        height: `${
          this.iframePreviewWidth.availableCanvasSize.height * (1 / scaleFactor)
        }px`
      };
    };

    if (uiStore.settings.scaleToFit === "ACTUAL_SIZE") {
      if (this.iframePreviewWidth.width === "100%") {
        return {
          width: "100%",
          minWidth: `${this.minWidth}px`
        };
      } else {
        return {
          width: `${this.iframePreviewWidth.width}px`
        };
      }
    } else {
      if (this.iframePreviewWidth.width === "100%") {
        if (this.iframePreviewWidth.availableCanvasSize.width < this.minWidth) {
          return scaledStyles(this.minWidth);
        } else {
          return {
            width: "100%",
            minWidth: `${this.minWidth}px`
          };
        }
      } else {
        return scaledStyles(this.iframePreviewWidth.width);
      }
    }
  }

  get iframeIsLoading() {
    return this.iframeState === "loading";
  }

  get isPreviewMode() {
    return uiStore.isPreviewMode;
  }

  handleIframeLoaded() {
    if (this.iframe?.contentWindow) {
      this.iframe.contentWindow.onunload = () => {
        this.$emit("iframeUnloaded");
      };
    }

    this.$emit("iframeLoaded", this.iframe);
  }

  handleReloadWanted() {
    this.$emit("reloadWanted");
  }

  mounted() {
    if (this.mainWrapper) {
      new ResizeObserver(this.setAvailableCanvasSize).observe(this.mainWrapper);
    } else {
      uiStore.logError("No canvas element.");
    }

    if (this.iframeWrapper) {
      new ResizeObserver(this.setIframeWrapperPosition).observe(
        this.iframeWrapper
      );
    } else {
      uiStore.logError("No iframe wrapper element.");
    }

    this.setAvailableCanvasSize();
  }

  setAvailableCanvasSize() {
    if (this.mainWrapper) {
      const rect = this.mainWrapper.getBoundingClientRect();

      const newPreviewWidth: CanvasPreviewWidth = {
        ...this.iframePreviewWidth,
        availableCanvasSize: {
          width: rect.width - this.canvasPadding * 2,
          height: rect.height - this.canvasPadding * 2
        }
      };

      this.$emit("previewWidthChange", newPreviewWidth);
    }

    // resizing the available canvas could move the iframe wrapper (for example, when the properties panel is toggled),
    // so when the resize observer calls setAvailableCanvasSize, we want to also call setIframeWrapperPosition
    this.setIframeWrapperPosition();
  }

  setIframeWrapperPosition() {
    if (this.iframeWrapper) {
      const rect = this.iframeWrapper.getBoundingClientRect();
      this.iframeWrapperPosition = {
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height
      };
    }
  }
}
